// Create-client form. After success the SPA writes the plaintext to
// sessionStorage so the new client's detail page can re-display it.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  api,
  type ClientCreateRequest,
  type ClientCreateResponse,
} from "../lib/api";
import {
  buttonStyle,
  Card,
  FieldLabel,
  inputStyle,
  PageHeader,
  tokens,
} from "../lib/theme";

const DEFAULT_FORM: ClientCreateRequest = {
  client_name: "",
  full_price: "0.00",
  partial_price: "0.00",
  partial_match_ratio: 0.5,
  rps_cap: 10,
};

export default function ClientNew() {
  const navigate = useNavigate();
  const [form, setForm] = useState<ClientCreateRequest>(DEFAULT_FORM);
  const [created, setCreated] = useState<ClientCreateResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  // No on-unmount wipe — sessionStorage holds the plaintext for the same
  // client's detail page. Wiped on logout/tab close.
  useEffect(() => undefined, []);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await api.createClient(form);
      setCreated(res);
      try {
        sessionStorage.setItem(
          `loop-rtb:apikey:${res.client_id}`,
          JSON.stringify({ value: res.api_key_plaintext, ts: Date.now() }),
        );
      } catch {
        /* sessionStorage may be unavailable */
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create");
    } finally {
      setSubmitting(false);
    }
  };

  const onCopy = async (): Promise<void> => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.api_key_plaintext);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard may be blocked */
    }
  };

  if (created) {
    return (
      <div>
        <PageHeader
          title="Client created"
          subtitle={
            <span>
              <strong style={{ color: tokens.color.fg0 }}>
                {created.client_name}
              </strong>{" "}
              <span style={{ fontFamily: tokens.font.mono }}>
                ({created.client_id})
              </span>
            </span>
          }
        />
        <Card>
          <div
            style={{
              padding: tokens.space.lg,
              background: tokens.color.bg1,
              border: "1px solid rgba(0,212,170,0.30)",
              borderRadius: tokens.radius.md,
              marginBottom: tokens.space.lg,
            }}
          >
            <div
              style={{
                fontSize: tokens.font.size.xs,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: tokens.color.tealLight,
                fontWeight: 600,
                marginBottom: tokens.space.sm,
              }}
            >
              Plaintext API key
            </div>
            <pre
              style={{
                margin: 0,
                padding: "10px 12px",
                background: tokens.color.bg0,
                color: tokens.color.tealLight,
                borderRadius: tokens.radius.sm,
                fontFamily: tokens.font.mono,
                fontSize: tokens.font.size.sm,
                overflow: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                userSelect: "all",
              }}
            >
              <code>{created.api_key_plaintext}</code>
            </pre>
            <div
              style={{
                marginTop: tokens.space.sm,
                display: "flex",
                gap: tokens.space.sm,
                alignItems: "center",
              }}
            >
              <button
                type="button"
                onClick={() => void onCopy()}
                style={buttonStyle("subtle")}
              >
                {copied ? "Copied ✓" : "Copy"}
              </button>
              <span
                style={{ color: tokens.color.fg2, fontSize: tokens.font.size.xs }}
              >
                Visible until you sign out or close this tab.
              </span>
            </div>
          </div>
          <div style={{ display: "flex", gap: tokens.space.sm, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() =>
                navigate(`/clients/${encodeURIComponent(created.client_id)}`)
              }
              style={buttonStyle("primary")}
            >
              Open client
            </button>
            <button
              type="button"
              onClick={() => navigate("/clients")}
              style={buttonStyle("ghost")}
            >
              Back to list
            </button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="New client"
        subtitle="Mint a bearer key for an integration partner"
      />
      <Card>
        <form onSubmit={onSubmit}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: tokens.space.lg,
            }}
          >
            <div>
              <FieldLabel htmlFor="new-client-name">Client name</FieldLabel>
              <input
                id="new-client-name"
                required
                value={form.client_name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, client_name: e.target.value }))
                }
                style={inputStyle}
              />
            </div>
            <div>
              <FieldLabel htmlFor="new-full-price">Full price (USD)</FieldLabel>
              <input
                id="new-full-price"
                required
                inputMode="decimal"
                value={form.full_price}
                onChange={(e) =>
                  setForm((f) => ({ ...f, full_price: e.target.value }))
                }
                style={inputStyle}
              />
            </div>
            <div>
              <FieldLabel htmlFor="new-partial-price">
                Partial price (USD)
              </FieldLabel>
              <input
                id="new-partial-price"
                required
                inputMode="decimal"
                value={form.partial_price}
                onChange={(e) =>
                  setForm((f) => ({ ...f, partial_price: e.target.value }))
                }
                style={inputStyle}
              />
            </div>
            <div>
              <FieldLabel htmlFor="new-ratio">
                Partial match ratio (0–1)
              </FieldLabel>
              <input
                id="new-ratio"
                required
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={form.partial_match_ratio}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    partial_match_ratio: Number(e.target.value),
                  }))
                }
                style={inputStyle}
              />
            </div>
            <div>
              <FieldLabel htmlFor="new-rps">RPS cap</FieldLabel>
              <input
                id="new-rps"
                required
                type="number"
                min="1"
                max="500"
                value={form.rps_cap}
                onChange={(e) =>
                  setForm((f) => ({ ...f, rps_cap: Number(e.target.value) }))
                }
                style={inputStyle}
              />
            </div>
          </div>
          {error && (
            <div
              role="alert"
              style={{
                marginTop: tokens.space.lg,
                padding: tokens.space.md,
                background: tokens.color.dangerBg,
                border: `1px solid ${tokens.color.dangerBorder}`,
                color: tokens.color.danger,
                borderRadius: tokens.radius.sm,
                fontSize: tokens.font.size.sm,
              }}
            >
              {error}
            </div>
          )}
          <div
            style={{
              marginTop: tokens.space.lg,
              display: "flex",
              gap: tokens.space.sm,
            }}
          >
            <button
              type="submit"
              disabled={submitting}
              style={buttonStyle("primary", submitting)}
            >
              {submitting ? "Creating…" : "Create client"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/clients")}
              disabled={submitting}
              style={buttonStyle("ghost", submitting)}
            >
              Cancel
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
}
